import React from "react";
import UploadExcelDialog from "../utils/UploadExcelEnhanced";
import useExcelUpload, {
  generateRandomAmount,
  generateRandomYear,
} from "../../../utils/hooks/useExcelUpload";
import { countries, incomeTypes } from "./calculations";

const instructions = [
  ["Prior Year Unremitted Foreign Income Upload Instructions"],
  [""],
  ["Columns"],
  ["1. Income Type - Type of foreign income"],
  [`Valid Values: ${incomeTypes.map((type) => type.label).join(", ")}`],
  ["2. Country (optional) - Country where income was earned"],
  ["Leave blank to group the income under Unspecified"],
  [`Valid Values: ${countries.map((type) => type.label).join(", ")}`],
  [
    "3. YA in which income is earned - Year of Assessment when income was earned",
  ],
  ["Format: 4-digit year (e.g., 2023, 2024)"],
  ["4. Available Income - Income available for the year"],
  ["Format: Numeric value (whole numbers only). Example: 50000"],
  ["5. Allowable Expenses - Allowable expenses for the year"],
  ["Format: Numeric value (whole numbers only). Example: 5000"],
  ["6. Foreign taxes suffered - Foreign taxes paid on the income"],
  ["Format: Numeric value (up to 2 decimal places). Example: 1500.00"],
  [""],
  ["Instructions"],
  [
    "1. Fill in your prior year unremitted income data in the 'Prior Year Income' worksheet",
  ],
  ["2. Ensure all required fields are filled correctly"],
  ["3. Verify data accuracy and formatting"],
  ["4. Save the file and upload it using the application"],
  [
    "5. The rows will be added to the 'Prior Year Unremitted Foreign Income' section",
  ],
  [""],
  ["Important Notes"],
  [
    "- Income types and any specified countries must match the valid values exactly (case-sensitive)",
  ],
  ["- YA must be a 4-digit year"],
  ["- Numeric values should not contain currency symbols or commas"],
  ["- Foreign taxes suffered can have up to 2 decimal places"],
  ["- Empty cells for numeric fields will default to 0"],
  ["- Currency should match your tax computation currency"],
];

const ForeignIncomeTrackerExcelUpload = ({
  onExcelAddRows,
  open,
  handleCloseDialog,
}) => {
  const {
    selectedFile,
    uploading,
    alertMessage,
    alertSeverity,
    errorFileUrl,
    rowCount,
    handleCloseAlert,
    handleDownloadTemplate,
    handleFileChange,
    handleResetFile,
    handleUpload,
    getTemplateHeadersString,
  } = useExcelUpload({
    fileName: "prior_year_unremitted_income_upload_template.xlsx",
    instructions,
    sampleData: Array.from({ length: 5 }, (_, index) => ({
      "Income Type": incomeTypes[index].label,
      Country: countries[index].label,
      "YA in which income is earned": generateRandomYear(),
      "Available Income": (() => {
        const income = generateRandomAmount();
        return income;
      })(),
      "Allowable Expenses": (() => {
        // Ensure that allowable expenses are less than available income
        const income = Number.isFinite(this?.["Available Income"])
          ? this["Available Income"]
          : generateRandomAmount();
        return Math.floor(income * (Math.random() * 0.7 + 0.01)); // up to 70% of income, always at least 1% of income
      })(),
      "Foreign taxes suffered": `1500.00`,
    })),
    validationSchema: [
      {
        type: "text",
        required: true,
        fixedValue: incomeTypes.map((row) => row.label),
      },
      {
        type: "text",
        round: true,
        required: false,
        fixedValue: countries.map((row) => row.label),
      },
      { type: "number", round: true, required: true },
      { type: "number", round: true, required: true },
      { type: "number", round: true, required: true },
      { type: "number", round: true, required: true },
    ],
    onUpload: (excelData, rowCount) => {
      if (rowCount > 0) {
        const updatedData = [];

        excelData.forEach((row) => {
          const [
            incomeType,
            country,
            yaEarned,
            amount,
            allowableExpenses,
            foreignTaxSuffered,
          ] = row;
          updatedData.push({
            incomeType,
            country,
            yaEarned,
            amount,
            allowableExpenses,
            foreignTaxSuffered,
          });
        });

        onExcelAddRows(updatedData);
      }
    },
  });

  const fixedValueGroups = [
    {
      label: "Income Type",
      values: incomeTypes.map((row) => row.label),
    },
    {
      label: "Country",
      values: countries.map((row) => row.label),
    },
  ];

  return (
    <UploadExcelDialog
      open={open}
      selectedFile={selectedFile}
      uploading={uploading}
      alertMessage={alertMessage}
      alertSeverity={alertSeverity}
      errorFileUrl={errorFileUrl}
      validRows={rowCount}
      onClose={handleCloseDialog}
      onDownloadTemplate={handleDownloadTemplate}
      onFileChange={handleFileChange}
      onUpload={handleUpload}
      onCloseAlert={handleCloseAlert}
      onReset={handleResetFile}
      templateHeadersString={getTemplateHeadersString()}
      templateInstructions={instructions}
      fixedValueGroups={fixedValueGroups}
    />
  );
};

export default ForeignIncomeTrackerExcelUpload;
